import { HIDDEN_TOOLS } from './toolLabels';

/**
 * Folds the stored and live events of a conversation into what the chat renders. Pure: the
 * same events always give the same view, so a reload (replay) and a tab that stayed open agree.
 */

function latestAssistantTurn(state, event) {
	const last = state.turns[state.turns.length - 1];
	if (last && last.role === 'assistant') return last;
	const turn = { role: 'assistant', seq: event.seq, createdAt: event.createdAt, text: null, steps: [], blocks: [] };
	state.turns.push(turn);
	return turn;
}

/**
 * The open proposal ends: `confirmed` when its mandate was applied ("Suche starten"),
 * `withdrawn` when a chip, the profile, a message, a stop or a new proposal overtook it.
 */
function closeOpenProposal(state, outcome) {
	const block = state.openProposalSeq === null ? null : state.plansBySeq.get(state.openProposalSeq);
	if (block && outcome && !block.confirmed && !block.withdrawn) block[outcome] = true;
	state.openProposalSeq = null;
}

const withdrawsProposal = (payload) => 'openProposalSeq' in payload && payload.openProposalSeq === null;

function upsertStep(state, event) {
	if (HIDDEN_TOOLS.has(event.payload.tool)) return null;
	const payload = { seq: event.seq, createdAt: event.createdAt, ...event.payload };
	const existing = state.stepsById.get(payload.operationId);
	if (existing) {
		Object.assign(existing, payload, { summary: payload.summary ?? existing.summary });
		return existing;
	}
	state.stepsById.set(payload.operationId, payload);
	latestAssistantTurn(state, event).steps.push(payload);
	return payload;
}

const HANDLERS = {
	user: (state, event) => {
		state.turns.push({ role: 'user', seq: event.seq, createdAt: event.createdAt, text: event.payload.text });
	},
	message: (state, event) => {
		state.turns.push({
			role: 'assistant',
			seq: event.seq,
			createdAt: event.createdAt,
			step: event.payload.step,
			text: event.payload.text,
			expects: event.payload.expects ?? null,
			steps: [],
			blocks: [],
		});
	},
	profile: (state, event) => {
		state.profile = event.payload.profile;
		if (withdrawsProposal(event.payload)) closeOpenProposal(state, 'withdrawn');
	},
	mandate: (state, event) => {
		state.mandate = event.payload.mandate;
		if (event.payload.estimate) state.estimate = event.payload.estimate;
		// The server applies the plan's mandate on confirmation (by: proposal); a user's chip
		// change withdraws the proposal instead.
		if (withdrawsProposal(event.payload)) {
			closeOpenProposal(state, event.payload.by === 'proposal' ? 'confirmed' : 'withdrawn');
		}
	},
	plan: (state, event) => {
		closeOpenProposal(state, 'withdrawn');
		if (event.payload.estimate) state.estimate = event.payload.estimate;
		const turn = latestAssistantTurn(state, event);
		turn.blocks.push({ type: 'profile', seq: event.seq });
		const block = {
			type: 'plan',
			seq: event.seq,
			proposalSeq: event.payload.proposalSeq ?? event.seq,
			steps: event.payload.steps ?? [],
			estimate: event.payload.estimate ?? null,
			mandate: event.payload.mandate ?? null,
			baseMandate: event.payload.baseMandate ?? null,
			widens: Boolean(event.payload.widens),
			widenings: event.payload.widenings ?? [],
			qualification: event.payload.qualification ?? null,
			preview: event.payload.preview ?? [],
			confirmed: false,
			withdrawn: false,
		};
		turn.blocks.push(block);
		state.plansBySeq.set(block.proposalSeq, block);
		state.openProposalSeq = block.proposalSeq;
	},
	status: (state, event) => {
		state.status = event.payload.status;
		if (event.payload.phase) state.phase = event.payload.phase;
		if (event.payload.usage) state.usage = event.payload.usage;
		if (event.payload.error) state.lastError = event.payload.error;
		if (event.payload.operationIds) state.waitingOperationIds = event.payload.operationIds;
		if (withdrawsProposal(event.payload)) closeOpenProposal(state, 'withdrawn');
		// Older events carry no openProposalSeq: work starting consumes the proposal.
		else if (!('openProposalSeq' in event.payload) && event.payload.status === 'running')
			closeOpenProposal(state, null);
	},
	action: upsertStep,
	commit: (state, event) => {
		upsertStep(state, event);
		if (event.payload.usage) state.usage = event.payload.usage;
	},
	funnel: (state, event) => {
		const { operationId, checked = 0, fit = 0, unfit = 0, unknown = 0, stale = false } = event.payload;
		const step = state.stepsById.get(operationId);
		if (step)
			Object.assign(step, { status: 'ok', summary: { ...step.summary, checked, fit, unfit, unknown, stale } });
	},
	preview: (state, event) => {
		state.previewCompanyIds = event.payload.companyIds ?? [];
	},
	results: (state, event) => {
		state.results = { ...event.payload, seq: event.seq };
		if (event.payload.previewCompanyIds?.length) state.previewCompanyIds = event.payload.previewCompanyIds;
		latestAssistantTurn(state, event).blocks.push({ type: 'results', seq: event.seq, ...event.payload });
	},
	saved: (state, event) => {
		state.saved = event.payload;
		latestAssistantTurn(state, event).blocks.push({ type: 'saved', seq: event.seq, ...event.payload });
	},
	error: (state, event) => {
		latestAssistantTurn(state, event).blocks.push({ type: 'error', seq: event.seq, text: event.payload.message });
	},
};

/**
 * @param {Array<{seq: number, type: string, payload: object, createdAt?: string}>} events - In sequence order
 * @returns {{
 *   lastSeq: number,
 *   status: string|null,
 *   phase: string|null,
 *   profile: object|null,
 *   mandate: object|null,
 *   estimate: object|null,
 *   usage: object|null,
 *   lastError: string|null,
 *   waitingOperationIds: string[],
 *   openProposalSeq: number|null,
 *   results: object|null,
 *   saved: object|null,
 *   previewCompanyIds: string[],
 *   turns: Array<object>, - user turns `{role: 'user', text}`, assistant turns with `steps` (tool cards) and `blocks`
 *   pendingActions: Array<object>,
 *   lastStep: number
 * }}
 */
export function buildConversation(events = []) {
	const state = {
		lastSeq: 0,
		status: null,
		phase: null,
		profile: null,
		mandate: null,
		estimate: null,
		usage: null,
		lastError: null,
		waitingOperationIds: [],
		openProposalSeq: null,
		plansBySeq: new Map(),
		results: null,
		saved: null,
		previewCompanyIds: [],
		turns: [],
		stepsById: new Map(),
	};
	for (const event of events) {
		HANDLERS[event.type]?.(state, event);
		state.lastSeq = Math.max(state.lastSeq, event.seq ?? 0);
	}
	const pendingActions = [];
	let lastStep = 0;
	for (const step of state.stepsById.values()) {
		lastStep = Math.max(lastStep, step.step ?? 0);
		if (step.status === 'pending') pendingActions.push(step);
	}
	const { stepsById: _stepsById, ...rest } = state;
	return { ...rest, pendingActions, lastStep };
}

/**
 * Unites a stored list with a fetched one by sequence number, in order.
 *
 * @param {Array<object>|undefined} existing
 * @param {Array<object>|undefined} fetched
 * @returns {Array<object>}
 */
export function mergeRunEvents(existing, fetched) {
	const bySeq = new Map();
	for (const event of [...(existing ?? []), ...(fetched ?? [])]) bySeq.set(event.seq, event);
	return [...bySeq.values()].sort((a, b) => a.seq - b.seq);
}

/**
 * Appends a live event, ignoring replays of sequence numbers already present.
 *
 * @param {Array<object>|undefined} events
 * @param {object} event
 * @returns {Array<object>}
 */
export function appendRunEvent(events, event) {
	const list = events ?? [];
	if (list.some((known) => known.seq === event.seq)) return list;
	return [...list, event];
}
